import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import type { PasswordRequirements } from '@t/checkout.types';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';

// ---------------------------------------------------------------------------
// Icons (ported from console frontend)
// ---------------------------------------------------------------------------

const CheckMarkIcon = ({ size = 14, color }: { size?: number; color?: string }) => (
  <SvgIcon sx={{ fontSize: size, color }} viewBox="0 0 24 24">
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </SvgIcon>
);

const CloseXIcon = ({ size = 14, color }: { size?: number; color?: string }) => (
  <SvgIcon sx={{ fontSize: size, color }} viewBox="0 0 24 24">
    <path d="m0 0h24v24h-24z" fill="none" />
    <path d="m22 4.0143-2.0143-2.0143-7.9857 7.9857-7.9857-7.9857-2.0143 2.0143 7.9857 7.9857-7.9857 7.9857 2.0143 2.0143 7.9857-7.9857 7.9857 7.9857 2.0143-2.0143-7.9857-7.9857z" />
  </SvgIcon>
);

const EyeIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M0 0h24v24H0V0z" fill="none" />
    <path d="M12 6c3.79 0 7.17 2.13 8.82 5.5C19.17 14.87 15.79 17 12 17s-7.17-2.13-8.82-5.5C4.83 8.13 8.21 6 12 6m0-2C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5c1.38 0 2.5 1.12 2.5 2.5S13.38 14 12 14s-2.5-1.12-2.5-2.5S10.62 9 12 9m0-2c-2.48 0-4.5 2.02-4.5 4.5S9.52 16 12 16s4.5-2.02 4.5-4.5S14.48 7 12 7z" />
  </SvgIcon>
);

const EyeOffIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M0 0h24v24H0V0zm0 0h24v24H0V0zm0 0h24v24H0V0zm0 0h24v24H0V0z" fill="none" />
    <path d="M12 6c3.79 0 7.17 2.13 8.82 5.5-.59 1.22-1.42 2.27-2.41 3.12l1.41 1.41c1.39-1.23 2.49-2.77 3.18-4.53C21.27 7.11 17 4 12 4c-1.27 0-2.49.2-3.64.57l1.65 1.65C10.66 6.09 11.32 6 12 6zm-1.07 1.14L13 9.21c.57.25 1.03.71 1.28 1.28l2.07 2.07c.08-.34.14-.7.14-1.07C16.5 9.01 14.48 7 12 7c-.37 0-.72.05-1.07.14zM2.01 3.87l2.68 2.68C3.06 7.83 1.77 9.53 1 11.5 2.73 15.89 7 19 12 19c1.52 0 2.98-.29 4.32-.82l3.42 3.42 1.41-1.41L3.42 2.45 2.01 3.87zm7.5 7.5l2.61 2.61c-.04.01-.08.02-.12.02-1.38 0-2.5-1.12-2.5-2.5 0-.05.01-.08.01-.13zm-3.4-3.4l1.75 1.75c-.23.55-.36 1.15-.36 1.78 0 2.48 2.02 4.5 4.5 4.5.63 0 1.23-.13 1.77-.36l.98.98c-.88.24-1.8.38-2.75.38-3.79 0-7.17-2.13-8.82-5.5.7-1.43 1.72-2.61 2.93-3.53z" />
  </SvgIcon>
);

const DiceIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
    <circle cx="7.5" cy="16.5" r="1.5" />
    <circle cx="7.5" cy="7.5" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="16.5" cy="16.5" r="1.5" />
    <circle cx="16.5" cy="7.5" r="1.5" />
  </SvgIcon>
);

// ---------------------------------------------------------------------------
// Password generator (ported from console frontend)
// ---------------------------------------------------------------------------

const SPECIAL_CHARACTERS = '~`!@#$%^&*()_+-=[]{};\':"|,.<>/?';

const secureRandom = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

const randomChar = (from: number, range: number) => String.fromCharCode(Math.floor(secureRandom() * range) + from);
const randomCapital = () => randomChar(65, 26);
const randomLower = () => randomChar(97, 26);
const randomSpecial = () => SPECIAL_CHARACTERS[Math.floor(secureRandom() * SPECIAL_CHARACTERS.length)];

const generateRandomPassword = (): string => {
  const targetLength = Math.floor(secureRandom() * 13) + 12; // 12–24
  const uuid = crypto.randomUUID();
  const chars: string[] = [];

  for (const ch of uuid) {
    if (chars.length >= targetLength) break;
    if (chars.length === 11 && !/[A-Z]/.test(chars.join(''))) {
      chars.push(randomCapital());
    }
    if (/[a-z]/.test(ch)) chars.push(randomLower());
    else if (/[0-9]/.test(ch)) {
      const nums = (chars.join('').match(/[0-9]/g) ?? []).length;
      chars.push(nums >= 6 ? randomCapital() : ch);
    } else if (ch === '-') {
      const s = randomSpecial();
      chars.push(chars.join('').includes(s) ? randomCapital() : s);
    }
  }

  return chars.join('');
};

// ---------------------------------------------------------------------------
// Strength bar
// ---------------------------------------------------------------------------

const STRENGTH_COLORS = ['#D1D5DB', '#EF4444', '#F59E0B', '#3B82F6', '#22C55E'] as const;
const STRENGTH_LABELS = ['N/A', 'Weak', 'Fair', 'Good', 'Strong'] as const;

const StrengthBar = ({ active, score }: { active: boolean; score: number }) => (
  <Box
    sx={{
      flex: 1,
      height: '5px',
      borderRadius: '84px',
      backgroundColor: active ? STRENGTH_COLORS[score] : STRENGTH_COLORS[0],
    }}
  />
);

// ---------------------------------------------------------------------------
// Requirement check row
// ---------------------------------------------------------------------------

interface RequirementRowProps {
  met: boolean | null; // null = not yet typed
  text: string;
}

const RequirementRow = ({ met, text }: RequirementRowProps) => {
  const color = met === null ? '#8A94A6' : met ? '#22C55E' : '#EF4444';
  return (
    <Stack direction="row" gap="6px" alignItems="center">
      {met ? <CheckMarkIcon size={14} color={color} /> : <CloseXIcon size={14} color={color} />}
      <Typography variant="body.sm" sx={{ color }}>
        {text}
      </Typography>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Input sx
// ---------------------------------------------------------------------------

const inputSx = {
  '& .MuiOutlinedInput-input::placeholder': { fontSize: '16px', fontWeight: 400, lineHeight: '100%' },
  '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '6px' } },
  '& .MuiOutlinedInput-input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px #fff inset',
    WebkitTextFillColor: 'inherit',
  },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PasswordFieldProps {
  password: string;
  confirmPassword: string;
  requirements?: PasswordRequirements | null;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onPasswordBlur?: () => void;
  onConfirmPasswordBlur?: () => void;
  onGenerate?: (generated: string) => void;
  passwordError?: string | null;
  confirmPasswordError?: string | null;
  passwordTouched?: boolean;
  confirmPasswordTouched?: boolean;
}

const PasswordField = ({
  password,
  confirmPassword,
  requirements,
  onPasswordChange,
  onConfirmPasswordChange,
  onPasswordBlur,
  onConfirmPasswordBlur,
  onGenerate,
  passwordError,
  confirmPasswordError,
  passwordTouched,
  confirmPasswordTouched,
}: PasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const hasInput = password.length > 0;

  const validationChecks = useMemo(() => {
    const checks: { text: string; met: boolean | null }[] = [];

    if (requirements) {
      const lengthText =
        requirements.minLength > 0 && requirements.maxLength > 0
          ? `Must be between ${requirements.minLength} and ${requirements.maxLength} characters long.`
          : requirements.minLength > 0
            ? `Must be at least ${requirements.minLength} characters long.`
            : null;
      if (lengthText)
        checks.push({
          text: lengthText,
          met: hasInput
            ? password.length >= requirements.minLength &&
              (requirements.maxLength <= 0 || password.length <= requirements.maxLength)
            : null,
        });
      if (requirements.lowercase > 0)
        checks.push({
          text: 'Must include at least one lowercase character.',
          met: hasInput ? /[a-z]/.test(password) : null,
        });
      if (requirements.uppercase > 0)
        checks.push({
          text: 'Must include at least one uppercase character.',
          met: hasInput ? /[A-Z]/.test(password) : null,
        });
      if (requirements.digit > 0)
        checks.push({ text: 'Must include at least one number.', met: hasInput ? /[0-9]/.test(password) : null });
      if (requirements.special > 0)
        checks.push({
          text: 'Must include at least one special character.',
          met: hasInput ? /[`~!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) : null,
        });
    } else {
      checks.push(
        { text: 'Must be at least 12 characters long.', met: hasInput ? password.length >= 12 : null },
        { text: 'Must include at least one lowercase character.', met: hasInput ? /[a-z]/.test(password) : null },
        { text: 'Must include at least one uppercase character.', met: hasInput ? /[A-Z]/.test(password) : null },
        { text: 'Must include at least one number.', met: hasInput ? /[0-9]/.test(password) : null },
        {
          text: 'Must include at least one special character.',
          met: hasInput ? /[`~!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) : null,
        }
      );
    }
    return checks;
  }, [password, requirements, hasInput]);

  const score = useMemo(() => {
    if (!hasInput) return 0;
    const metCount = validationChecks.filter((c) => c.met === true).length;
    const total = validationChecks.length;
    if (total === 0) return 0;
    const ratio = metCount / total;
    if (ratio <= 0.25) return 1;
    if (ratio <= 0.5) return 2;
    if (ratio < 1) return 3;
    return 4;
  }, [validationChecks, hasInput]);

  const handleGenerate = () => {
    const generated = generateRandomPassword();
    onPasswordChange(generated);
    onConfirmPasswordChange(generated);
    onGenerate?.(generated);
    setShowPassword(true);
    setShowConfirm(true);
  };

  return (
    <Stack gap="15px">
      {/* New Password */}
      <Stack gap="10px">
        <TextFieldLabel label="New Password" />
        <TextField
          sx={inputSx}
          type={showPassword ? 'text' : 'password'}
          placeholder="Enter password..."
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          onBlur={() => {
            if (!password) onConfirmPasswordChange('');
            onPasswordBlur?.();
          }}
          autoComplete="one-time-code"
          error={passwordTouched && !!passwordError}
          helperText={passwordTouched && passwordError ? <FormErrorMessage>{passwordError}</FormErrorMessage> : null}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Generate Unique Password" arrow placement="top">
                  <IconButton onClick={handleGenerate} edge="end" size="small">
                    <DiceIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Strength bar */}
      <Stack direction="row" gap="8px">
        <StrengthBar active={hasInput && score >= 1} score={score} />
        <StrengthBar active={hasInput && score >= 2} score={score} />
        <StrengthBar active={hasInput && score >= 3} score={score} />
        <StrengthBar active={hasInput && score >= 4} score={score} />
      </Stack>

      <Typography variant="subtitle.card">
        Password Strength: {hasInput ? STRENGTH_LABELS[score] : STRENGTH_LABELS[0]}
      </Typography>

      {/* Requirement checklist */}
      <Stack gap="10px">
        {validationChecks.map((check) => (
          <RequirementRow key={check.text} met={check.met} text={check.text} />
        ))}
      </Stack>

      {/* Confirm Password */}
      <Stack gap="10px">
        <TextFieldLabel label="Confirm New Password" />
        <TextField
          sx={inputSx}
          type={showConfirm ? 'text' : 'password'}
          placeholder="Confirm password..."
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          onBlur={onConfirmPasswordBlur}
          disabled={!password}
          autoComplete="one-time-code"
          error={confirmPasswordTouched && !!confirmPasswordError}
          helperText={
            confirmPasswordTouched && confirmPasswordError ? (
              <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
            ) : null
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small">
                  {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </Stack>
  );
};

export default PasswordField;
